// Offer AI Summary — API calls (transaction-service, via the gateway).
// Self-contained: builds its own URLs from API_BASE_URL so config/api.js is
// not modified. Mirrors docAiSummaryApi.js (same auth + base pattern).
import { onAuthStateChanged } from 'firebase/auth';
import { API_BASE_URL, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';

const base = (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offer-summaries`;

const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(false).then(resolve).catch(reject);
    return;
  }
  const timer = setTimeout(() => reject(new Error('Auth timeout')), 10000);
  const unsub = onAuthStateChanged(auth, (user) => {
    unsub();
    clearTimeout(timer);
    if (user) user.getIdToken(false).then(resolve).catch(reject);
    else reject(new Error('Not authenticated'));
  });
});

const authHeaders = async () => ({ Authorization: `Bearer ${await getFirebaseToken()}` });

export const listOfferAiSummaries = async (transactionId) => {
  const res = await apiClient.get(base(transactionId), { headers: await authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
};

export const getOfferAiSummary = async (transactionId, summaryId) => {
  const res = await apiClient.get(`${base(transactionId)}/${summaryId}`, {
    headers: await authHeaders(),
  });
  return res.data;
};

export const regenerateOfferAiSummary = async (transactionId, summaryId) => {
  const res = await apiClient.post(
    `${base(transactionId)}/${summaryId}:regenerate`,
    null,
    { headers: await authHeaders() },
  );
  return res.data;
};

// User-initiated first generation — called from the Generate button when no
// summary record exists yet for an offer.
export const generateOfferAiSummary = async (transactionId, offerId) => {
  const res = await apiClient.post(
    `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/offer-summary`,
    null,
    { headers: await authHeaders() },
  );
  return res.data;
};

// Prefer the backend's user-facing message when there is one.
export const errorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 429) return message || 'Summaries are busy right now. Please try again in a minute.';
  if (status === 403) return "You don't have access to this offer's summary.";
  if (message && typeof message === 'string') return message;
  return fallback;
};
